// H-1 — shared contract-header seam. The X-Correlation-Id [STD-API-5] and Idempotency-Key
// [STD-API-4] injection lived inline in @zercode/api-client; extracted here as a single pure
// function so any fetch/axios caller applies the contract identically. Framework-agnostic and
// zero-dependency (matches the @zercode/utils charter). Accept-Language stays in api-client
// (it's i18n-coupled via @zercode/i18n) — not a generic contract header.
//
// ⚠ THE HEADER IS `X-Correlation-Id`, WITH THE PREFIX. This sent `Correlation-Id` until 2026-08-20,
// and the server reads `X-Correlation-Id` (CorrelationIdMiddleware.HeaderName) — so for three months
// every SPA request's correlation id was silently discarded and the server minted its own. Nothing
// broke visibly, because the response envelope's `meta.correlationId` carries the server's value, so
// error reports still had *an* id. What was lost is the thing the header exists for: one id shared
// between the browser and the server logs. The canonical name is pinned by two e2e suites —
// `tests/e2e/specs/hello.spec.ts` and `BillingHttpE2ETests.Correlation_id_is_echoed`.

/**
 * The correlation header [STD-API-5], named once. Must match the server's
 * `CorrelationIdMiddleware.HeaderName` exactly — a mismatch is silent in both directions.
 */
export const CORRELATION_HEADER = 'X-Correlation-Id';

/** HTTP methods that mutate state and therefore REQUIRE an Idempotency-Key [STD-API-4]. */
export const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export interface ContractHeaderResult {
  /** Input headers plus X-Correlation-Id (always) and Idempotency-Key (writes only). */
  headers: Record<string, string>;
  /** The X-Correlation-Id applied — surfaced so the caller can thread it into error/ProblemDetails. */
  correlationId: string;
  /** The Idempotency-Key applied (writes only) — surfaced so a 401-retry reuses the SAME key. */
  idempotencyKey?: string;
}

export interface ContractHeaderOptions {
  /** Override the generated Idempotency-Key (retry of the SAME logical write). */
  idempotencyKey?: string;
  /** Id generator (defaults to crypto.randomUUID); injectable so tests are deterministic. */
  generateId?: () => string;
}

/**
 * Returns a NEW header map with the mandatory contract headers applied (input is not mutated):
 * X-Correlation-Id on every request, Idempotency-Key on mutating methods. These are authoritative —
 * they are applied last so they can't be dropped by caller-supplied headers.
 */
export function applyContractHeaders(
  method: string,
  headers: Record<string, string>,
  options: ContractHeaderOptions = {}
): ContractHeaderResult {
  const generate = options.generateId ?? (() => crypto.randomUUID());
  const correlationId = generate();
  const out: Record<string, string> = { ...headers, [CORRELATION_HEADER]: correlationId };

  let idempotencyKey: string | undefined;
  if (MUTATING_METHODS.has(method.toUpperCase())) {
    idempotencyKey = options.idempotencyKey ?? generate();
    out['Idempotency-Key'] = idempotencyKey;
  }

  return { headers: out, correlationId, idempotencyKey };
}
